import React, {FC, useCallback, useEffect, useMemo, useRef, useState} from "react";
import {useNavigate} from "react-router-dom";
import {
    Checkbox,
    DefaultButton,
    MaskedTextField,
    Pivot,
    PivotItem,
    PrimaryButton,
    Stack,
    Text,
    TextField,
} from "@fluentui/react";
import Axios from "axios";
import {useBoolean} from "@fluentui/react-hooks";
import {useTranslation} from "react-i18next";
import {
    ApplicationUser,
    Company,
    Contact,
    ContactCompany,
    ContactEmail,
    PagedResult,
    SupportPriorityEnum,
    SupportSourceEnum,
    SupportStatusEnum,
    SupportTicket,
    SupportTicketTimeLog,
    SupportTicketTimeLogDto,
    SupportTicketSingleDto,
} from "../API";
import {AppRoute} from "../AppRoutes";
import {CustomSearchBox} from "./CustomSearchBox";
import {DropdownFromEnum} from "./DropdownFromEnum";
import {DropdownFromList} from "./DropdownFromList";
import {LocalDatePicker} from "./LocalDatePicker";
import {ShowApiError} from "./ShowApiError";
import {IModelError} from "../interfaces/IModelError";
import {TenantResource} from "../enums/TenantResource";
import {useLocalStorage} from "../helpers/useLocalStorage";
import {iconDataFromStatus, SupportStatusIcon} from "./SupportStatusIcon";
import {ticketTypes} from "../index";
import {WorkOutsideWorkingHoursBadge} from "./WorkOutsideWorkingHoursBadge";
import {
    parseTimeLogDurationToMinutes,
    roundTimeLogDurationUpToQuarterHour,
    suggestTimeLogDuration,
} from "../helpers/TimeLogDurationHelper";
import {defaultNewTimeLogBilling} from "../helpers/SupportTimeLogDefaults";

type ActivityTimeLogMode = "existing" | "new";
type ActivityTimeLogTab = ActivityTimeLogMode | "activities";

export type ActivityTimeLogSourceType = "teamviewer" | "waka" | "pantheon";

export interface ActivityTimeLogTicketDefaults {
    title?: string;
    companyId?: number;
    companyName?: string;
    contactId?: number;
    contactEmailId?: number;
    status?: SupportStatusEnum;
}

export interface ActivityTimeLogSourceOption {
    key: string;
    type: ActivityTimeLogSourceType;
    title: string;
    subtitle?: string;
    timeLabel?: string;
    duration?: string | number;
    startedAt?: Date | string;
    teamViewerSessionId?: string;
    wakaTimeWorklogId?: number;
    pantheonSessionId?: number;
    workOutsideWorkingHoursPreview?: boolean | null;
}

interface IActivityTimeLogFormProps {
    baseDto: Partial<SupportTicketTimeLogDto & SupportTicketTimeLog> & Record<string, any>;
    currentTicket?: Partial<SupportTicket>;
    user?: ApplicationUser;
    defaultTicket?: ActivityTimeLogTicketDefaults;
    defaultMode?: ActivityTimeLogMode;
    teamViewerDeviceIdForContactLookup?: string;
    activitySourceOptions?: ActivityTimeLogSourceOption[];
    defaultActivitySourceKeys?: string[];
    primaryActivitySourceKey?: string;
    activitySourceRoundingMinutes?: number | null;
    prepareTimeLogDto?: () => Promise<Partial<SupportTicketTimeLogDto & SupportTicketTimeLog> | undefined>;
    onSaved?: (result: { ticketId?: number; timeLogId?: number; activitySourceKeys?: string[] }) => void;
}

const asDate = (value?: Date | string) => {
    if (!value) {
        return new Date();
    }

    return value instanceof Date ? value : new Date(value);
};

const hasContactEmails = (contact?: Contact) => !!contact?.emails?.length;

const unique = <T,>(items: T[]) => Array.from(new Set(items));

export const ActivityTimeLogForm: FC<IActivityTimeLogFormProps> = ({
                                                                       baseDto,
                                                                       currentTicket,
                                                                       user,
                                                                       defaultTicket,
                                                                       defaultMode,
                                                                       teamViewerDeviceIdForContactLookup,
                                                                       activitySourceOptions,
                                                                       defaultActivitySourceKeys,
                                                                       primaryActivitySourceKey,
                                                                       activitySourceRoundingMinutes,
                                                                       prepareTimeLogDto,
                                                                       onSaved,
                                                                   }) => {
    const {t} = useTranslation();
    const navigate = useNavigate();
    const [lastFastStatus, setLastFastStatus] = useLocalStorage<SupportStatusEnum>(
        "fast-ticket-default-status",
        SupportStatusEnum.Open,
    );
    const [mode, setMode] = useState<ActivityTimeLogMode>(
        defaultMode ?? (currentTicket?.id || baseDto.ticketId ? "existing" : "new"),
    );
    const [selectedTab, setSelectedTab] = useState<ActivityTimeLogTab>(
        defaultMode ?? (currentTicket?.id || baseDto.ticketId ? "existing" : "new"),
    );
    const [selectedActivitySourceKeys, setSelectedActivitySourceKeys] = useState<string[]>(
        () => unique(
            defaultActivitySourceKeys?.length
                ? defaultActivitySourceKeys
                : primaryActivitySourceKey
                    ? [primaryActivitySourceKey]
                    : [],
        ),
    );
    const [selectedTicket, setSelectedTicket] = useState<Partial<SupportTicket> | undefined>(
        currentTicket ?? (baseDto.ticketIdstorage as any),
    );
    const [company, setCompany] = useState<Company | undefined>(
        defaultTicket?.companyId
            ? ({
                id: defaultTicket.companyId,
                name: defaultTicket.companyName ?? "",
            } as Company)
            : undefined,
    );
    const [contactCompany, setContactCompany] = useState<ContactCompany>();
    const [contactCompanies, setContactCompanies] = useState<ContactCompany[]>();
    const [contactEmail, setContactEmail] = useState<ContactEmail>();
    const [contactEmails, setContactEmails] = useState<ContactEmail[]>();
    const [ticketTitle, setTicketTitle] = useState(
        defaultTicket?.title || baseDto.note || "",
    );
    const [ticketStatus, setTicketStatus] = useState<SupportStatusEnum>(
        defaultTicket?.status ?? lastFastStatus ?? SupportStatusEnum.Open,
    );
    const initialWorkOutsideWorkingHours =
        (baseDto as any).workOutsideWorkingHours === true ||
        (baseDto as any).workOutsideWorkingHoursPreview === true;
    const [timeLogData, setTimeLogData] = useState<
        Partial<SupportTicketTimeLogDto & SupportTicketTimeLog> & Record<string, any>
    >({
        ...baseDto,
        agentId: baseDto.agentId ?? user?.id,
        date: asDate(baseDto.date as any),
        billable: baseDto.billable ?? true,
        invoice: baseDto.invoice ?? true,
        time: (baseDto.time as any) ?? "00:00",
        billableTime: (baseDto.billableTime as any) ?? "00:00",
        note: baseDto.note ?? "",
        technicalNote: baseDto.technicalNote ?? "",
        type: baseDto.type ?? "",
        workOutsideWorkingHours: initialWorkOutsideWorkingHours,
    });
    const [outsideWorkingHoursPreview, setOutsideWorkingHoursPreview] = useState<
        boolean | null | undefined
    >((baseDto as any).workOutsideWorkingHoursPreview);
    const [apiError, setApiError] = useState<IModelError<any>>();
    const [loading, {setTrue: setLoading, setFalse: setNotLoading}] =
        useBoolean(false);

    const loadContactCompaniesForCompany = useCallback((companyId?: number) => {
        if (!companyId) {
            setContactCompanies(undefined);
            setContactCompany(undefined);
            setContactEmails(undefined);
            setContactEmail(undefined);
            return;
        }

        Axios.post<PagedResult<ContactCompany>>("ContactCompany/List", {
            query: {
                companyId: {
                    equals: companyId,
                },
            },
            paging: {
                count: 0,
                page: -1,
            },
        }).then(({data}) => {
            const items = data.items ?? [];
            setContactCompanies(items);
            const preferred =
                items.find((item) => item.contactId === defaultTicket?.contactId) ??
                items.find((item) => item.isPrimary) ??
                items[0];
            setContactCompany(preferred);
        });
    }, [defaultTicket?.contactId]);

    useEffect(() => {
        loadContactCompaniesForCompany(company?.id);
    }, [company?.id, loadContactCompaniesForCompany]);

    useEffect(() => {
        if (!company?.id || company.name) {
            return;
        }

        Axios.get<Company>(`Company/${company.id}`)
            .then(({data}) => setCompany(data))
            .catch(() => {
            });
    }, [company?.id, company?.name]);

    useEffect(() => {
        const emails = contactCompany?.contact?.emails ?? [];
        setContactEmails(emails);
        setContactEmail(
            emails.find((email) => email.id === defaultTicket?.contactEmailId) ??
            emails.find((email) => email.isPrimary) ??
            emails[0],
        );
    }, [contactCompany, defaultTicket?.contactEmailId]);

    useEffect(() => {
        if (!teamViewerDeviceIdForContactLookup || company?.id) {
            return;
        }

        const deviceId = Number(teamViewerDeviceIdForContactLookup);
        if (Number.isNaN(deviceId)) {
            return;
        }

        Axios.get<Contact>("GetTeamViewerDeviceIdContact", {
            params: {deviceId},
        }).then(({data}) => {
            if (!data?.id) {
                return;
            }

            Axios.post<PagedResult<ContactCompany>>("ContactCompany/List", {
                query: {
                    contactId: {
                        equals: data.id,
                    },
                },
                paging: {
                    count: 0,
                    page: -1,
                },
            }).then(({data: contactCompanyData}) => {
                const items = contactCompanyData.items ?? [];
                const preferred =
                    items.find((item) => item.isPrimary) ??
                    items[0];

                if (preferred?.company) {
                    setCompany(preferred.company);
                }

                setContactCompanies(items);
                setContactCompany(preferred);
            });
        }).catch(() => {
        });
    }, [company?.id, teamViewerDeviceIdForContactLookup]);

    const selectedTicketId = selectedTicket?.id ?? baseDto.ticketId;
    const initialBilling = useRef({
        ticketId: selectedTicketId,
        billable: baseDto.billable,
        invoice: baseDto.invoice,
        preservesInvoice: baseDto.id != null,
    });
    const [billingTicket, setBillingTicket] = useState<SupportTicketSingleDto>();
    const [billingLoading, setBillingLoading] = useState(false);
    const [billingFailed, setBillingFailed] = useState(false);
    useEffect(() => {
        let cancelled = false;
        setBillingTicket(undefined);
        setBillingFailed(false);
        if (mode !== "existing" || !selectedTicketId) {
            setBillingLoading(false);
            const billing = defaultNewTimeLogBilling(undefined, user);
            setTimeLogData((current) => ({
                ...current,
                billable: initialBilling.current.billable ?? billing.billable,
                invoice: initialBilling.current.preservesInvoice
                    ? initialBilling.current.invoice
                    : billing.invoice,
            }));
            return;
        }
        setBillingLoading(true);
        Axios.get<SupportTicketSingleDto>(`Support/${selectedTicketId}`)
            .then(({data}) => {
                if (cancelled) return;
                setBillingTicket(data);
                const billing = defaultNewTimeLogBilling(data, user);
                setTimeLogData((current) => ({
                    ...current,
                    billable: billing.billable &&
                        (selectedTicketId !== initialBilling.current.ticketId || initialBilling.current.billable !== false),
                    invoice: billing.invoice &&
                        (!initialBilling.current.preservesInvoice || initialBilling.current.invoice === true),
                }));
            })
            .catch(() => {
                if (cancelled) return;
                setBillingFailed(true);
                setApiError({ticketId: [t("billingSettingsLoadFailed")]} as any);
            })
            .finally(() => {
                if (!cancelled) setBillingLoading(false);
            });
        return () => {
            cancelled = true;
        };
    }, [mode, selectedTicketId, user, t]);

    const teamViewerSessionId = timeLogData.teamViewerSessionId as string | undefined;
    const pantheonSessionId = timeLogData.pantheonSessionId as number | undefined;
    const baseOutsideWorkingHoursPreview = (baseDto as any).workOutsideWorkingHoursPreview;
    const hasActivitySourceOptions = !!activitySourceOptions?.length;
    const activitySourceByKey = useMemo(
        () => new Map((activitySourceOptions ?? []).map((source) => [source.key, source])),
        [activitySourceOptions],
    );
    const selectedActivitySources = useMemo(
        () =>
            selectedActivitySourceKeys
                .map((key) => activitySourceByKey.get(key))
                .filter(Boolean) as ActivityTimeLogSourceOption[],
        [activitySourceByKey, selectedActivitySourceKeys],
    );
    const selectedActivityPatch = useMemo(() => {
        if (!hasActivitySourceOptions) {
            return undefined;
        }

        const teamViewerSessionIds = selectedActivitySources
            .map((source) => source.teamViewerSessionId)
            .filter(Boolean) as string[];
        const wakaTimeWorklogIds = selectedActivitySources
            .map((source) => source.wakaTimeWorklogId)
            .filter((id) => id !== undefined && id !== null) as number[];
        const pantheonSessionIds = selectedActivitySources
            .map((source) => source.pantheonSessionId)
            .filter((id) => id !== undefined && id !== null) as number[];

        return {
            teamViewerSessionId: teamViewerSessionIds[0],
            teamViewerSessionIds,
            wakaTimeWorklogId: wakaTimeWorklogIds[0],
            wakaTimeWorklogIds,
            pantheonSessionId: pantheonSessionIds[0],
            pantheonSessionIds,
        };
    }, [hasActivitySourceOptions, selectedActivitySources]);
    const selectedOutsideWorkingHoursPreview = useMemo(() => {
        if (!hasActivitySourceOptions) {
            return undefined;
        }

        if (selectedActivitySources.some((source) => source.workOutsideWorkingHoursPreview === true)) {
            return true;
        }

        if (selectedActivitySources.some((source) => source.workOutsideWorkingHoursPreview !== undefined &&
            source.workOutsideWorkingHoursPreview !== null)) {
            return false;
        }

        return undefined;
    }, [hasActivitySourceOptions, selectedActivitySources]);

    useEffect(() => {
        if (!hasActivitySourceOptions || !selectedActivityPatch) {
            return;
        }

        const totalMinutes = selectedActivitySources.reduce(
            (sum, source) => sum + parseTimeLogDurationToMinutes(source.duration),
            0,
        );
        const suggestedTime = suggestTimeLogDuration(totalMinutes, activitySourceRoundingMinutes);
        const firstStartedAt = selectedActivitySources
            .map((source) => source.startedAt)
            .filter(Boolean)
            .map((value) => asDate(value as any))
            .sort((left, right) => left.getTime() - right.getTime())[0];

        setOutsideWorkingHoursPreview(selectedOutsideWorkingHoursPreview);
        setTimeLogData((current) => ({
            ...current,
            ...selectedActivityPatch,
            time: suggestedTime,
            billableTime: current.billable || current.invoice
                ? roundTimeLogDurationUpToQuarterHour(suggestedTime)
                : "00:00",
            date: firstStartedAt ?? current.date,
            workOutsideWorkingHours:
                !!current.workOutsideWorkingHours || selectedOutsideWorkingHoursPreview === true,
        }));
    }, [
        activitySourceRoundingMinutes,
        hasActivitySourceOptions,
        selectedActivityPatch,
        selectedActivitySources,
        selectedOutsideWorkingHoursPreview,
    ]);

    useEffect(() => {
        if (hasActivitySourceOptions) {
            return;
        }

        if (baseOutsideWorkingHoursPreview !== undefined) {
            setOutsideWorkingHoursPreview(baseOutsideWorkingHoursPreview);
            if (baseOutsideWorkingHoursPreview === true) {
                setTimeLogData((current) => ({
                    ...current,
                    workOutsideWorkingHours: true,
                }));
            }
            return;
        }

        if (!teamViewerSessionId && !pantheonSessionId) {
            setOutsideWorkingHoursPreview(undefined);
            return;
        }

        let cancelled = false;
        Axios.get<boolean | null>("SupportTimeLog/OutsideWorkingHoursPreview", {
            params: {
                teamViewerSessionId,
                pantheonSessionId,
                ticketId: timeLogData.ticketId,
            },
        })
            .then(({data}) => {
                if (cancelled) {
                    return;
                }

                setOutsideWorkingHoursPreview(data);
                if (data === true) {
                    setTimeLogData((current) => ({
                        ...current,
                        workOutsideWorkingHours: true,
                    }));
                }
            })
            .catch(() => {
                if (!cancelled) {
                    setOutsideWorkingHoursPreview(undefined);
                }
            });

        return () => {
            cancelled = true;
        };
    }, [baseOutsideWorkingHoursPreview, hasActivitySourceOptions, pantheonSessionId, teamViewerSessionId, timeLogData.ticketId]);
    const showOutsideWorkingHoursWarning =
        outsideWorkingHoursPreview === true || !!timeLogData.workOutsideWorkingHours;

    const buildTimeLogDto = useCallback(async (ticketId?: number) => {
        const prepared = await prepareTimeLogDto?.();
        return {
            ...timeLogData,
            ...(prepared ?? {}),
            ...(selectedActivityPatch ?? {}),
            ticketId,
            date: asDate(timeLogData.date as any),
        };
    }, [prepareTimeLogDto, selectedActivityPatch, timeLogData]);

    const saveExistingTicket = useCallback(async () => {
        if (billingLoading || billingFailed) return;
        if (!selectedTicketId) {
            setApiError({ticketId: [t("TicketId is required")]} as any);
            return;
        }

        setLoading();
        setApiError(undefined);
        try {
            await Axios.post("SupportTimeLog", await buildTimeLogDto(selectedTicketId));
            onSaved?.({ticketId: selectedTicketId, activitySourceKeys: selectedActivitySourceKeys});
        } catch (error: any) {
            const data = error?.response?.data;
            setApiError(data?.errors ?? data);
        } finally {
            setNotLoading();
        }
    }, [billingLoading, billingFailed, buildTimeLogDto, onSaved, selectedActivitySourceKeys, selectedTicketId, setLoading, setNotLoading, t]);

    const saveNewTicket = useCallback(async () => {
        if (!company?.id) {
            setApiError({companyId: ["Company is required."]} as any);
            return;
        }

        setLoading();
        setApiError(undefined);
        try {
            const timeLog = await buildTimeLogDto(undefined);
            const {data} = await Axios.post("SupportTimeLog/CreateTicketAndTimeLog", {
                ticket: {
                    title: ticketTitle?.trim() || timeLog.note,
                    companyId: company.id,
                    contactId: contactCompany?.contactId,
                    contactEmailId: contactEmail?.id,
                    status: ticketStatus,
                    priority: SupportPriorityEnum.Medium,
                    source: SupportSourceEnum.System,
                },
                timeLog,
            });

            setLastFastStatus(ticketStatus);
            onSaved?.({...data, activitySourceKeys: selectedActivitySourceKeys});
            if (data?.ticketId) {
                navigate(AppRoute.EDIT_SUPPORT_TICKET(data.ticketId));
            }
        } catch (error: any) {
            const data = error?.response?.data;
            setApiError(data?.errors ?? data);
        } finally {
            setNotLoading();
        }
    }, [
        buildTimeLogDto,
        company?.id,
        contactCompany?.contactId,
        contactEmail?.id,
        navigate,
        onSaved,
        selectedActivitySourceKeys,
        setLastFastStatus,
        setLoading,
        setNotLoading,
        ticketStatus,
        ticketTitle,
    ]);

    const save = useCallback(() => {
        if (mode === "existing") {
            saveExistingTicket();
        } else {
            saveNewTicket();
        }
    }, [mode, saveExistingTicket, saveNewTicket]);

    const statusIcon = useMemo(() => iconDataFromStatus(ticketStatus).icon, [ticketStatus]);
    const toggleActivitySource = useCallback((key: string, checked?: boolean) => {
        setSelectedActivitySourceKeys((current) => {
            if (checked) {
                return unique([...current, key]);
            }

            const next = current.filter((item) => item !== key);
            return next.length ? next : current;
        });
    }, []);
    const ungroupActivities = useCallback(() => {
        const leaderKey = primaryActivitySourceKey ?? selectedActivitySourceKeys[0];

        if (leaderKey) {
            setSelectedActivitySourceKeys([leaderKey]);
        }
    }, [primaryActivitySourceKey, selectedActivitySourceKeys]);

    return (
        <Stack
            tokens={{childrenGap: 10, padding: 10}}
            style={{minWidth: 320, width: "min(760px, calc(100vw - 48px))"}}
        >
            <Pivot
                selectedKey={selectedTab}
                onLinkClick={(item) => {
                    const key = item?.props.itemKey as ActivityTimeLogTab | undefined;

                    if (!key) {
                        return;
                    }

                    setSelectedTab(key);
                    if (key === "existing" || key === "new") {
                        setMode(key);
                    }
                }}
            >
                <PivotItem headerText="Postojeći ticket" itemKey="existing">
                    <Stack tokens={{childrenGap: 8}} style={{paddingTop: 8}}>
                        <CustomSearchBox<SupportTicket>
                            label="Ticket"
                            placeholder="Ticket"
                            searchUrl="SupportTicketCRUD/List"
                            tenantResource={TenantResource.SupportTicketCRUD}
                            type="DEFAULT_CRUD"
                            listItemKey="id"
                            titleKey="title"
                            subtitleKey="id"
                            selected={selectedTicket as SupportTicket}
                            optionSelected={setSelectedTicket as any}
                            fullWidth
                        />
                        {currentTicket?.id ? (
                            <DefaultButton
                                disabled={loading || selectedTicketId === currentTicket.id}
                                text="Poveži s trenutnim ticketom"
                                iconProps={{iconName: "Link"}}
                                onClick={() => setSelectedTicket(currentTicket)}
                            />
                        ) : null}
                        <ShowApiError errors={apiError} keys={["ticketId", "TicketId"]}/>
                    </Stack>
                </PivotItem>
                <PivotItem headerText="Novi ticket" itemKey="new">
                    <Stack tokens={{childrenGap: 8}} style={{paddingTop: 8}}>
                        <TextField
                            disabled={loading}
                            label={t("title")}
                            value={ticketTitle}
                            onChange={(_, value) => setTicketTitle(value ?? "")}
                        />
                        <ShowApiError errors={apiError} keys={["title", "Title"]}/>
                        <CustomSearchBox<Company>
                            label={t("company")}
                            placeholder={t("company")}
                            searchUrl="Company/List"
                            tenantResource={TenantResource.Company}
                            type="DEFAULT_CRUD"
                            listItemKey="id"
                            titleKey="name"
                            selected={company}
                            optionSelected={setCompany}
                            fullWidth
                        />
                        <ShowApiError errors={apiError} keys={["companyId", "CompanyId"]}/>
                        {contactCompanies?.length ? (
                            <DropdownFromList<ContactCompany>
                                disabled={loading}
                                label={t("contact")}
                                list={contactCompanies}
                                value={contactCompany}
                                setValue={setContactCompany}
                                toKND={(item) => ({
                                    key: item.id,
                                    data: item,
                                    name: item.contact?.fullName ?? "-",
                                })}
                            />
                        ) : company?.id ? (
                            <Text variant="small">Nema kontakata za odabrano poduzeće.</Text>
                        ) : null}
                        <ShowApiError errors={apiError} keys={["contactId", "ContactId"]}/>
                        {hasContactEmails(contactCompany?.contact) ? (
                            <DropdownFromList<ContactEmail>
                                disabled={loading}
                                label="E-mail"
                                list={contactEmails ?? []}
                                value={contactEmail}
                                setValue={setContactEmail}
                                toKND={(item) => ({
                                    key: item.id,
                                    data: item,
                                    name: item.email,
                                })}
                            />
                        ) : null}
                        <ShowApiError errors={apiError} keys={["contactEmailId", "ContactEmailId"]}/>
                        <DropdownFromEnum
                            disabled={loading}
                            value={ticketStatus}
                            setValue={(value) => setTicketStatus(value ?? SupportStatusEnum.Open)}
                            label="status"
                            object={SupportStatusEnum}
                            rightSideIconButtonProps={{iconProps: {iconName: statusIcon}}}
                            iconFromValue={(status) => <SupportStatusIcon status={status}/>}
                        />
                    </Stack>
                </PivotItem>
                {hasActivitySourceOptions ? (
                    <PivotItem headerText={`Aktivnosti (${selectedActivitySourceKeys.length})`} itemKey="activities">
                        <Stack tokens={{childrenGap: 8}} style={{paddingTop: 8}}>
                            <Stack horizontal verticalAlign="center" tokens={{childrenGap: 8}} wrap>
                                <DefaultButton
                                    disabled={loading || selectedActivitySourceKeys.length <= 1}
                                    text="Razgrupiraj"
                                    iconProps={{iconName: "UngroupObject"}}
                                    onClick={ungroupActivities}
                                />
                                <Text variant="small">
                                    Grupirano: {selectedActivitySourceKeys.length}
                                </Text>
                            </Stack>
                            <Stack tokens={{childrenGap: 6}}>
                                {(activitySourceOptions ?? []).map((source) => {
                                    const isPrimary = source.key === primaryActivitySourceKey;
                                    const label = `${source.title}${isPrimary ? " · vođa grupe" : ""}`;

                                    return (
                                        <Stack
                                            key={source.key}
                                            tokens={{childrenGap: 2}}
                                            styles={{
                                                root: {
                                                    border: isPrimary ? "1px solid #c8e6c9" : "1px solid #e1dfdd",
                                                    borderRadius: 4,
                                                    padding: 6,
                                                },
                                            }}
                                        >
                                            <Checkbox
                                                disabled={loading}
                                                label={label}
                                                checked={selectedActivitySourceKeys.includes(source.key)}
                                                onChange={(_, checked) => toggleActivitySource(source.key, checked)}
                                            />
                                            {source.subtitle || source.timeLabel ? (
                                                <Text variant="small" styles={{root: {color: "#605e5c"}}}>
                                                    {[source.subtitle, source.timeLabel].filter(Boolean).join(" · ")}
                                                </Text>
                                            ) : null}
                                        </Stack>
                                    );
                                })}
                            </Stack>
                        </Stack>
                    </PivotItem>
                ) : null}
            </Pivot>

            <Stack horizontal tokens={{childrenGap: 8}} wrap>
                <Stack grow>
                    <CustomSearchBox<ApplicationUser>
                        label={t("agent")}
                        placeholder={t("agent")}
                        searchUrl="SupportUsers/List"
                        tenantResource={TenantResource.SupportUsers}
                        type="DEFAULT_CRUD"
                        listItemKey="id"
                        titleKey="firstName"
                        subtitleKey="userName"
                        selected={(timeLogData as any).agentIdstorage ?? user}
                        optionSelected={(agent) =>
                            setTimeLogData({
                                ...timeLogData,
                                agentId: agent?.id,
                                agentIdstorage: agent,
                            })
                        }
                        fullWidth
                    />
                    <ShowApiError errors={apiError} keys={["agentId", "AgentId"]}/>
                </Stack>
                <Stack grow>
                    <LocalDatePicker
                        disabled={loading}
                        label={t("date")}
                        value={asDate(timeLogData.date as any)}
                        onSelectDate={(date) =>
                            setTimeLogData({...timeLogData, date: date ?? undefined})
                        }
                    />
                    <ShowApiError errors={apiError} keys={["date", "Date"]}/>
                </Stack>
            </Stack>
            <Stack horizontal tokens={{childrenGap: 8}} wrap>
                <Stack grow>
                    <MaskedTextField
                        disabled={loading}
                        label="Vrijeme rada"
                        mask="99:99"
                        value={timeLogData.time as any}
                        onChange={(_, value) => setTimeLogData({...timeLogData, time: value as any})}
                    />
                    <ShowApiError errors={apiError} keys={["time", "Time"]}/>
                </Stack>
                <Stack grow>
                    <MaskedTextField
                        disabled={loading || (!timeLogData.billable && !timeLogData.invoice)}
                        label="Naplativo vrijeme rada"
                        mask="99:99"
                        value={timeLogData.billableTime as any}
                        onChange={(_, value) =>
                            setTimeLogData({...timeLogData, billableTime: value as any})
                        }
                    />
                    <ShowApiError errors={apiError} keys={["billableTime", "BillableTime"]}/>
                </Stack>
                <Stack verticalAlign="end" tokens={{childrenGap: 8}} style={{paddingBottom: 4}}>
                    <Checkbox
                        disabled={loading || billingLoading || billingTicket?.projectBillable === false}
                        label={t("billable")}
                        checked={!!timeLogData.billable}
                        onChange={(_, checked) =>
                            setTimeLogData({
                                ...timeLogData,
                                billable: checked,
                                invoice: checked ? timeLogData.invoice : false,
                            })
                        }
                    />
                    <Checkbox
                        disabled={loading || billingLoading || billingTicket?.projectInvoice === false}
                        label={t("invoice")}
                        checked={!!timeLogData.invoice}
                        onChange={(_, checked) =>
                            setTimeLogData({...timeLogData, invoice: checked})
                        }
                    />
                </Stack>
            </Stack>
            <Stack horizontal verticalAlign="center" tokens={{childrenGap: 8}} wrap>
                <Checkbox
                    disabled={loading}
                    label="Rad izvan radnog vremena"
                    checked={!!timeLogData.workOutsideWorkingHours}
                    onChange={(_, checked) =>
                        setTimeLogData({
                            ...timeLogData,
                            workOutsideWorkingHours: outsideWorkingHoursPreview === true || !!checked,
                        })
                    }
                />
                {showOutsideWorkingHoursWarning ? (
                    <WorkOutsideWorkingHoursBadge compact/>
                ) : null}
            </Stack>
            <DropdownFromList<string>
                disabled={loading}
                value={timeLogData.type}
                label={t("type")}
                list={ticketTypes}
                toKND={(item) => ({name: item, data: item, key: item})}
                setValue={(type) => setTimeLogData({...timeLogData, type})}
            />
            <TextField
                disabled={loading}
                label={t("note")}
                multiline
                rows={5}
                value={timeLogData.note}
                onChange={(_, value) => setTimeLogData({...timeLogData, note: value ?? ""})}
            />
            <ShowApiError errors={apiError} keys={["note", "Note", ""]}/>
            <TextField
                disabled={loading}
                label={t("technicalNote")}
                multiline
                rows={3}
                value={timeLogData.technicalNote}
                onChange={(_, value) =>
                    setTimeLogData({...timeLogData, technicalNote: value ?? ""})
                }
            />
            <Stack horizontal horizontalAlign="end" tokens={{childrenGap: 8}}>
                <PrimaryButton
                    disabled={loading || (mode === "existing" && (billingLoading || billingFailed))}
                    text={mode === "existing" ? "Spremi vrijeme rada" : "Izradi ticket i vrijeme rada"}
                    iconProps={{iconName: mode === "existing" ? "Clock" : "Add"}}
                    onClick={save}
                />
            </Stack>
        </Stack>
    );
};
